/**
 * Serviço de sincronização do timer entre múltiplas abas
 * Utiliza BroadcastChannel API para comunicação entre abas do mesmo domínio
 */

export enum TimerSyncMessageType {
  TIMER_RESET = 'TIMER_RESET',
  TIMER_STOP = 'TIMER_STOP',
  TIMER_PAUSE = 'TIMER_PAUSE',
  TIMER_RESUME = 'TIMER_RESUME',
  TIMER_START = 'TIMER_START',
  TIMER_ADD_TIME = 'TIMER_ADD_TIME',
  TIMER_SET_VISIBILITY = 'TIMER_SET_VISIBILITY',
  TIMER_RESTART = 'TIMER_RESTART',
  TIMER_TOGGLE_MODE = 'TIMER_TOGGLE_MODE',
}

export interface TimerSyncMessage {
  type: TimerSyncMessageType
  payload?: any
  timestamp: number
  source: string // ID único da aba que enviou a mensagem
}

type MessageHandler = (message: TimerSyncMessage) => void

class TimerSyncService {
  private channel: BroadcastChannel | null = null
  private messageHandlers: Set<MessageHandler> = new Set()
  private sourceId: string
  private isSupported: boolean

  constructor() {
    this.sourceId = this.generateSourceId()
    this.isSupported = typeof window !== 'undefined' && 'BroadcastChannel' in window

    if (this.isSupported) {
      try {
        this.channel = new BroadcastChannel('timer-sync')
        this.channel.onmessage = this.handleMessage.bind(this)
      } catch (error) {
        console.error('Erro ao criar BroadcastChannel:', error)
        this.isSupported = false
      }
    }
  }

  /**
   * Gera um ID único para esta instância/aba
   */
  private generateSourceId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Trata mensagens recebidas de outras abas
   */
  private handleMessage(event: MessageEvent<TimerSyncMessage>): void {
    const message = event.data

    // Ignora mensagens enviadas por esta própria aba
    if (message.source === this.sourceId) {
      return
    }

    // Notifica todos os handlers registrados
    this.messageHandlers.forEach((handler) => {
      try {
        handler(message)
      } catch (error) {
        console.error('Erro ao processar mensagem de sincronização:', error)
      }
    })
  }

  /**
   * Envia uma mensagem para todas as outras abas
   */
  public sendMessage(type: TimerSyncMessageType, payload?: any): void {
    if (!this.isSupported || !this.channel) {
      return
    }

    try {
      const message: TimerSyncMessage = {
        type,
        payload,
        timestamp: Date.now(),
        source: this.sourceId,
      }

      this.channel.postMessage(message)
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro ao enviar mensagem de sincronização:', error)
      }
    }
  }

  /**
   * Registra um handler para receber mensagens de outras abas
   */
  public onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler)

    // Retorna função para remover o handler
    return () => {
      this.messageHandlers.delete(handler)
    }
  }

  /**
   * Fecha o canal de comunicação
   */
  public close(): void {
    if (this.channel) {
      this.channel.close()
      this.channel = null
    }
    this.messageHandlers.clear()
  }

  /**
   * Verifica se o BroadcastChannel é suportado
   */
  public getSupported(): boolean {
    return this.isSupported
  }
}

// Singleton instance
let timerSyncInstance: TimerSyncService | null = null

/**
 * Obtém a instância singleton do serviço de sincronização
 */
export const getTimerSyncService = (): TimerSyncService => {
  if (!timerSyncInstance) {
    timerSyncInstance = new TimerSyncService()
  }
  return timerSyncInstance
}
